import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import type { ServiceDeskUser } from './viewModel';
import type {
  ComplaintFilters,
  ComplaintReportSummary,
  ContractTier,
  CreateComplaintPayload,
  CreateTicketPayload,
  KnowledgeArticle,
  ServiceComplaint,
  ServiceDeskSummary,
  ServiceTicket,
  SourceChannel,
  TicketCategory,
  TicketFilters,
  TicketListResponse,
} from './types';

const BASE = '/api/service-desk';

export const serviceDeskKeys = {
  root: ['service-desk'] as const,
  summary: ['service-desk', 'summary'] as const,
  tickets: (filters: TicketFilters) => ['service-desk', 'tickets', filters] as const,
  ticket: (id: string | null) => ['service-desk', 'ticket', id] as const,
  complaints: (filters: ComplaintFilters) => ['service-desk', 'complaints', filters] as const,
  complaint: (id: string | null) => ['service-desk', 'complaint', id] as const,
  complaintSummary: ['service-desk', 'complaint-summary'] as const,
  knowledge: (category: string) => ['service-desk', 'knowledge', category] as const,
};

export interface ServiceDeskAccess {
  operator: boolean;
  canCreateClientSupport: boolean;
  canManageKnowledge: boolean;
  canManageAssets: boolean;
  canManageIncidents: boolean;
  canAllocateAssets: boolean;
  canRevealPasswords: boolean;
}

/** What the signed-in person may do on the service desk, as the server decides it. */
export function useServiceDeskAccess() {
  return useQuery({
    queryKey: ['service-desk', 'access'],
    queryFn: async () => (await api.get<ServiceDeskAccess>(`${BASE}/access`)).data,
    staleTime: 60_000,
  });
}

/** The signed-in user, with whether they run the desk. */
export function useServiceDeskUser(): ServiceDeskUser | null {
  const user = useUser();
  const { data: access } = useServiceDeskAccess();
  return user ? { ...(user as ServiceDeskUser), operator: access?.operator ?? false } : null;
}

export interface ServiceDeskLookups {
  clients: { id: string; name: string; company: string }[];
  projects: { id: string; name: string; key: string }[];
  products: { id: string; name: string; sku: string }[];
}

/** Clients, projects and products to link a ticket or complaint to (the people who run the desk only). */
export function useServiceDeskLookups(enabled = true) {
  return useQuery({
    queryKey: ['service-desk', 'lookups'],
    queryFn: async () => (await api.get<ServiceDeskLookups>(`${BASE}/lookups`)).data,
    enabled,
    staleTime: 60_000,
  });
}

export function useServiceDeskSummary() {
  return useQuery({
    queryKey: serviceDeskKeys.summary,
    queryFn: async () => (await api.get<ServiceDeskSummary>(`${BASE}/summary?workflowType=standard`)).data,
    staleTime: 30_000,
  });
}

export function useServiceTickets(filters: TicketFilters) {
  return useQuery({
    queryKey: serviceDeskKeys.tickets(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.kind) params.set('kind', filters.kind);
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      params.set('limit', '100');
      return (await api.get<TicketListResponse>(`${BASE}/tickets?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useServiceTicket(id: string | null) {
  return useQuery({
    queryKey: serviceDeskKeys.ticket(id),
    queryFn: async () => (await api.get<ServiceTicket>(`${BASE}/tickets/${id}`)).data,
    enabled: !!id,
  });
}

export function useComplaintReportSummary() {
  return useQuery({
    queryKey: serviceDeskKeys.complaintSummary,
    queryFn: async () => (await api.get<ComplaintReportSummary>(`${BASE}/complaints/reports/summary`)).data,
    staleTime: 30_000,
  });
}

export function useServiceComplaints(filters: ComplaintFilters) {
  return useQuery({
    queryKey: serviceDeskKeys.complaints(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.cmsStatus) params.set('cmsStatus', filters.cmsStatus);
      if (filters.cmsCategory) params.set('cmsCategory', filters.cmsCategory);
      if (filters.complaintTypeId) params.set('complaintTypeId', filters.complaintTypeId);
      params.set('limit', '100');
      return (await api.get<{ data: ServiceComplaint[]; total: number; limit: number; offset: number }>(`${BASE}/complaints?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useServiceComplaint(id: string | null) {
  return useQuery({
    queryKey: serviceDeskKeys.complaint(id),
    queryFn: async () => (await api.get<ServiceComplaint>(`${BASE}/complaints/${id}`)).data,
    enabled: !!id,
  });
}

export function useKnowledgeArticles(category = '', enabled = true) {
  return useQuery({
    queryKey: serviceDeskKeys.knowledge(category),
    queryFn: async () => {
      const suffix = category ? `?category=${encodeURIComponent(category)}` : '';
      return (await api.get<KnowledgeArticle[]>(`${BASE}/knowledge${suffix}`)).data;
    },
    enabled,
  });
}

export function useServiceDeskMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: serviceDeskKeys.root });

  const createTicket = useMutation({
    mutationFn: async (payload: CreateTicketPayload) => (await api.post<ServiceTicket>(`${BASE}/tickets`, payload)).data,
    onSuccess: refresh,
  });
  const createComplaint = useMutation({
    mutationFn: async (payload: CreateComplaintPayload) => (await api.post<ServiceComplaint>(`${BASE}/complaints`, payload)).data,
    onSuccess: refresh,
  });
  const updateComplaint = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      (await api.patch<ServiceComplaint>(`${BASE}/complaints/${id}`, payload)).data,
    onSuccess: refresh,
  });
  const transitionComplaint = useMutation({
    mutationFn: async ({ id, toStatus, note }: { id: string; toStatus: ServiceComplaint['cmsStatus']; note?: string }) =>
      (await api.post<ServiceComplaint>(`${BASE}/complaints/${id}/transition`, { toStatus, note })).data,
    onSuccess: refresh,
  });
  const assignTicket = useMutation({
    mutationFn: async ({ id, assignedTo, assignedTeam, note }: { id: string; assignedTo: string; assignedTeam: string; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/assign`, { assignedTo, assignedTeam, note })).data,
    onSuccess: refresh,
  });
  const respondTicket = useMutation({
    mutationFn: async ({ id, note, channel }: { id: string; note: string; channel: SourceChannel }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/respond`, { note, channel })).data,
    onSuccess: refresh,
  });
  const updateTicketStatus = useMutation({
    mutationFn: async ({ id, status, note }: { id: string; status: 'in_progress' | 'waiting_requester'; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/status`, { status, note })).data,
    onSuccess: refresh,
  });
  const escalateTicket = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/escalate`, { note })).data,
    onSuccess: refresh,
  });
  const resolveTicket = useMutation({
    mutationFn: async ({ id, resolutionSummary, createKnowledgeDraft }: { id: string; resolutionSummary: string; createKnowledgeDraft: boolean }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/resolve`, { resolutionSummary, createKnowledgeDraft })).data,
    onSuccess: refresh,
  });
  const confirmTicket = useMutation({
    mutationFn: async ({ id, csat, note }: { id: string; csat?: number; note?: string }) =>
      (await api.post<ServiceTicket>(`${BASE}/tickets/${id}/confirm`, { csat, note })).data,
    onSuccess: refresh,
  });

  const createKnowledge = useMutation({
    mutationFn: async (payload: { title: string; body: string; category: TicketCategory; visibility: 'internal' | 'client' }) =>
      (await api.post<KnowledgeArticle>(`${BASE}/knowledge`, payload)).data,
    onSuccess: refresh,
  });
  const publishKnowledge = useMutation({
    mutationFn: async (id: string) => (await api.post<KnowledgeArticle>(`${BASE}/knowledge/${id}/publish`)).data,
    onSuccess: refresh,
  });

  return {
    createTicket,
    createComplaint,
    updateComplaint,
    transitionComplaint,
    assignTicket,
    respondTicket,
    updateTicketStatus,
    escalateTicket,
    resolveTicket,
    confirmTicket,
    createKnowledge,
    publishKnowledge,
  };
}

export type { ContractTier };
